import React, { FC } from 'react';
import { ReportProblem } from '@mui/icons-material';
import { Box, Drawer, Stack, Typography } from '@mui/material';

import { Button } from '../../components/core';
import { NAV_DRAWER_WIDTH } from '../../AppShell/AppShell';

export const SAVE_BAR_HEIGHT = 75;
const SAVE_BAR_HEIGHT_WITH_DETAIL = 96;
const SAVE_BAR_HEIGHT_WITH_EMPHASIZED_DETAIL = 104;

type SaveBarProps = {
  /**
   * Whether or not to display the save bar
   */
  show: boolean;
  /**
   * IDs for testing the button
   */
  dataTestId?: string;
  /**
   * Function to call when save button is clicked
   */
  onSave?: () => void;
  /**
   * Whether or not the save button is loading
   */
  saveLoading?: boolean;
  /**
   * Function to call when cancel button is clicked
   */
  onCancel?: () => void;
  /**
   * Text to display
   */
  text: string;
  /**
   * Optional second line (e.g. parent-hierarchy inheritance hint).
   */
  detailText?: string;
  /** Stronger styling for the detail line (e.g. parent-hierarchy inheritance warning). */
  emphasizeDetailText?: boolean;
  /**
   * Whether or not the Save button is disabled
   */
  disableSave?: boolean;
  /**
   * The id of the form to save
   */
  formId?: string;
};

const SaveBar: FC<SaveBarProps> = (props) => {
  const {
    show,
    onSave,
    onCancel,
    text,
    detailText,
    emphasizeDetailText = false,
    disableSave = false,
    saveLoading = false,
    dataTestId = '',
    formId,
  } = props;

  const barHeight = detailText
    ? emphasizeDetailText
      ? SAVE_BAR_HEIGHT_WITH_EMPHASIZED_DETAIL
      : SAVE_BAR_HEIGHT_WITH_DETAIL
    : SAVE_BAR_HEIGHT;

  return (
    <Drawer
      data-testid={dataTestId}
      sx={{ zIndex: 2, mt: 13 }}
      open={show}
      variant="persistent"
      anchor="bottom"
    >
      <Box
        sx={{
          minHeight: barHeight,
          marginLeft: { xs: 0, md: NAV_DRAWER_WIDTH },
          backgroundColor: 'background.light',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, sm: 4, md: 8 },
          py: detailText ? 1 : 0,
          gap: 1,
        }}
      >
        <Stack spacing={0.25} sx={{ pr: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: { xs: 12, sm: 14, md: 16 },
            }}
            data-testid={`${dataTestId}-SaveText`}
          >
            {text}
          </Typography>
          {detailText ? (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="flex-start"
              data-testid={`${dataTestId}-SaveDetailText`}
            >
              {emphasizeDetailText ? (
                <ReportProblem
                  aria-hidden
                  sx={{
                    color: 'error.dark',
                    fontSize: { xs: 18, sm: 20, md: 22 },
                    mt: 0.15,
                    flexShrink: 0,
                  }}
                />
              ) : null}
              <Typography
                component="span"
                sx={{
                  color: emphasizeDetailText ? 'error.dark' : 'text.secondary',
                  fontWeight: emphasizeDetailText ? 600 : 400,
                  fontSize: emphasizeDetailText
                    ? { xs: 14, sm: 15, md: 16 }
                    : { xs: 11, sm: 12, md: 13 },
                  lineHeight: emphasizeDetailText ? 1.4 : 1.35,
                }}
              >
                {detailText}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
        <Stack
          direction="row"
          spacing={{ xs: 1, sm: 2 }}
          sx={{ '& button': { width: { xs: '75px', sm: '88px' } } }}
        >
          {onCancel && (
            <Button
              theme="secondary"
              text="Cancel"
              onClick={onCancel}
              disabled={saveLoading}
              dataTestId={`${dataTestId}-CancelButton`}
            />
          )}
          <Button
            form={formId}
            type={formId ? 'submit' : 'button'}
            theme="primary"
            text="Save"
            onClick={onSave}
            loading={saveLoading}
            disabled={disableSave || saveLoading}
            dataTestId={`${dataTestId}-SaveButton`}
          />
        </Stack>
      </Box>
    </Drawer>
  );
};

export default SaveBar;
